import { I18n } from '@affine/i18n';
import {
  renderStreamLainNestedDisplayItemRow,
  streamLainDisclosureStyles,
} from '@blocksuite/affine-shared/utils';
import {
  CloseIcon,
  PausePanelIcon,
  PlayIcon,
  StopIcon,
} from '@blocksuite/icons/lit';
import type {
  GoalStepModelPolicy,
  GoalTreeEnvelope,
  GoalTreeNode,
  GoalTreePatch,
} from '@streamlain/view-config';
import { isGoalTreeModelPolicyEditableNode } from '@streamlain/view-config';
import { css, html, LitElement, nothing, unsafeCSS } from 'lit';
import { property, state } from 'lit/decorators.js';

import {
  aiChatBadgeTypography,
  aiChatControlTypography,
  aiChatHeadingTypography,
  aiChatMetaTypography,
  aiChatStatusTypography,
} from '../../styles/ai-chat-typography';
import type { GoalListSummary } from './goal-tree-context';
import {
  createGoalTreeViewModel,
  effectiveStepModelLabel,
  goalModeLabelForDisplay,
  goalNodeLabel,
  goalStatusLabel,
  nodeStatusTone,
  resolveActiveGoalNodeId,
} from './goal-tree-view-model';

export class GoalTreePanel extends LitElement {
  static override styles = [
    unsafeCSS(streamLainDisclosureStyles),
    css`
      :host {
        ${aiChatStatusTypography}
        --goal-tree-border: var(
          --affine-border-color,
          var(--streamlain-v2-layer-border, rgba(255, 255, 255, 0.14))
        );
        --goal-tree-hover: var(--affine-hover-color, rgba(255, 255, 255, 0.08));
        --goal-tree-surface: var(
          --affine-background-primary-color,
          var(--streamlain-v2-layer-background, #171717)
        );
        --goal-tree-surface-muted: var(
          --affine-background-overlay-panel-color,
          var(--streamlain-v2-input-background, #242424)
        );
        --goal-tree-text: var(
          --affine-text-primary-color,
          var(--streamlain-v2-text-primary, #f5f5f5)
        );
        --goal-tree-text-muted: var(
          --affine-text-secondary-color,
          var(--streamlain-v2-text-secondary, #a3a3a3)
        );
        display: block;
        width: 100%;
        height: 100%;
        min-height: 0;
        box-sizing: border-box;
        color: var(--goal-tree-text);
      }

      .goal-tree-panel {
        display: flex;
        flex-direction: column;
        width: 100%;
        height: 100%;
        min-height: 320px;
        box-sizing: border-box;
        overflow: hidden;
        border: 1px solid var(--goal-tree-border);
        border-right: 0;
        border-radius: 14px 0 0 14px;
        background: var(--goal-tree-surface);
        box-shadow: var(
          --affine-overlay-shadow,
          0 18px 50px rgba(0, 0, 0, 0.26)
        );
        color: var(--goal-tree-text);
      }

      .goal-tree-backdrop {
        display: none;
      }

      .goal-tree-header {
        display: grid;
        grid-template-columns: minmax(0, 1fr) auto;
        gap: 10px;
        align-items: start;
        padding: 12px 12px 8px;
        border-bottom: 1px solid var(--goal-tree-border);
      }

      .goal-tree-header-actions {
        display: flex;
        align-items: center;
        gap: 4px;
      }

      .goal-tree-header-main {
        display: flex;
        min-width: 0;
        align-items: flex-start;
        gap: 8px;
      }

      .goal-tree-header-copy {
        min-width: 0;
      }

      .goal-tree-control {
        display: inline-grid;
        width: 28px;
        height: 28px;
        place-items: center;
        padding: 0;
        border: 1px solid var(--goal-tree-border);
        border-radius: 6px;
        background: transparent;
        color: var(--goal-tree-text);
        cursor: pointer;
      }

      .goal-tree-control svg {
        width: 16px;
        height: 16px;
      }

      .goal-tree-control:hover {
        background: var(--goal-tree-hover);
      }

      .goal-tree-control:disabled {
        opacity: 0.55;
        cursor: default;
      }

      .goal-tree-title {
        ${aiChatHeadingTypography}
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .goal-tree-subtitle {
        ${aiChatMetaTypography}
        margin-top: 3px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        color: var(--goal-tree-text-muted);
      }

      .goal-tree-close {
        display: inline-grid;
        width: 28px;
        height: 28px;
        place-items: center;
        border: none;
        border-radius: 6px;
        background: transparent;
        color: currentColor;
        cursor: pointer;
      }

      .goal-tree-close:hover {
        background: var(--goal-tree-hover);
      }

      .goal-tree-summary {
        display: grid;
        grid-template-columns: repeat(3, minmax(0, 1fr));
        gap: 1px;
        background: var(--goal-tree-border);
        border-bottom: 1px solid var(--goal-tree-border);
      }

      .goal-tree-metric {
        min-width: 0;
        padding: 8px 10px;
        background: var(--goal-tree-surface-muted);
      }

      .goal-tree-metric-label {
        ${aiChatBadgeTypography}
        color: var(--goal-tree-text-muted);
      }

      .goal-tree-metric-value {
        ${aiChatControlTypography}
        margin-top: 2px;
        font-weight: 600;
      }

      .goal-tree-body {
        flex: 1 1 auto;
        min-height: 0;
        overflow: auto;
        padding: 8px 10px 12px;
      }

      .goal-tree-empty {
        ${aiChatStatusTypography}
        padding: 12px;
        color: var(--goal-tree-text-muted);
      }

      .goal-tree-history {
        display: grid;
        align-content: start;
        gap: 6px;
        flex: 1 1 auto;
        min-height: 0;
        overflow: auto;
        padding: 10px;
      }

      .goal-tree-history-item {
        display: grid;
        grid-template-columns: 10px minmax(0, 1fr) auto;
        align-items: center;
        gap: 10px;
        width: 100%;
        min-height: 52px;
        padding: 8px 10px;
        border: 1px solid var(--goal-tree-border);
        border-radius: 7px;
        background: var(--goal-tree-surface-muted);
        color: var(--goal-tree-text);
        font: inherit;
        text-align: left;
        cursor: pointer;
      }

      .goal-tree-history-item:hover {
        background: var(--goal-tree-hover);
      }

      .goal-tree-history-item:disabled {
        opacity: 0.6;
        cursor: default;
      }

      .goal-tree-history-title,
      .goal-tree-history-meta {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .goal-tree-history-title {
        font-weight: 500;
      }

      .goal-tree-history-meta,
      .goal-tree-history-progress {
        ${aiChatMetaTypography}
        color: var(--goal-tree-text-muted);
      }

      .goal-tree-history-back {
        margin-right: 4px;
        padding: 0 8px;
        width: auto;
      }

      .goal-tree-action-error {
        ${aiChatMetaTypography}
        padding: 7px 12px;
        border-bottom: 1px solid var(--goal-tree-border);
        color: var(--streamlain-error-color, #d64545);
      }

      .goal-tree-status-dot {
        width: 9px;
        height: 9px;
        border-radius: 50%;
        background: var(--affine-placeholder-color);
      }

      .goal-tree-status-dot[data-tone='positive'] {
        background: #1f9d55;
      }

      .goal-tree-status-dot[data-tone='critical'] {
        background: #d64545;
      }

      .goal-tree-status-dot[data-tone='warning'] {
        background: #c47f16;
      }

      .goal-tree-status-dot[data-tone='accent'] {
        background: #2563eb;
      }

      @keyframes goal-tree-active-step-pulse {
        0%,
        100% {
          box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.18);
        }
        50% {
          box-shadow: 0 0 0 5px rgba(37, 99, 235, 0.08);
        }
      }

      .goal-tree-node-active {
        background: color-mix(in srgb, #2563eb 10%, transparent);
      }

      .goal-tree-node-active .goal-tree-status-dot {
        background: #2563eb;
        animation: goal-tree-active-step-pulse 1.4s ease-in-out infinite;
      }

      .goal-tree-status-dot[data-tone='blocked'] {
        background: #9f1239;
      }

      .goal-tree-status-dot[data-tone='muted'] {
        background: #64748b;
      }

      .goal-tree-row-action {
        ${aiChatBadgeTypography}
        display: inline-flex;
        gap: 6px;
        align-items: center;
        max-width: 150px;
        color: var(--goal-tree-text-muted);
      }

      .goal-tree-row-action span {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .goal-tree-detail {
        display: grid;
        gap: 8px;
        padding: 10px;
        border-top: 1px solid var(--goal-tree-border);
        background: var(--goal-tree-surface-muted);
      }

      .goal-tree-detail-title {
        ${aiChatControlTypography}
        overflow-wrap: anywhere;
        font-weight: 600;
      }

      .goal-tree-detail-meta {
        ${aiChatMetaTypography}
        color: var(--goal-tree-text-muted);
      }

      .goal-tree-detail-section {
        display: grid;
        gap: 6px;
      }

      .goal-tree-detail-text {
        ${aiChatMetaTypography}
        color: var(--goal-tree-text-muted);
        overflow-wrap: anywhere;
      }

      .goal-tree-detail-input {
        display: grid;
        gap: 6px;
      }

      .goal-tree-detail-input input,
      .goal-tree-detail-input select {
        ${aiChatControlTypography}
        min-width: 0;
        height: 30px;
        border: 1px solid var(--goal-tree-border);
        border-radius: 6px;
        padding: 0 8px;
        background: var(--goal-tree-surface);
        color: var(--goal-tree-text);
      }

      .goal-tree-detail-label {
        ${aiChatBadgeTypography}
        color: var(--goal-tree-text-muted);
      }

      .goal-tree-detail-toggle {
        ${aiChatMetaTypography}
        display: flex;
        min-width: 0;
        align-items: center;
        gap: 8px;
        color: var(--goal-tree-text);
      }

      .goal-tree-detail-toggle input {
        width: 16px;
        height: 16px;
      }

      .goal-tree-detail-actions {
        display: flex;
        flex-wrap: wrap;
        gap: 6px;
      }

      .goal-tree-detail-content {
        display: grid;
        gap: 8px;
      }

      .goal-tree-detail-actions button {
        ${aiChatControlTypography}
        height: 28px;
        border: 1px solid var(--goal-tree-border);
        border-radius: 6px;
        background: var(--goal-tree-surface);
        color: var(--goal-tree-text);
        cursor: pointer;
      }

      .goal-tree-detail-error {
        ${aiChatMetaTypography}
        color: #b42318;
        overflow-wrap: anywhere;
      }

      @media (max-width: 720px) {
        .goal-tree-panel {
          border-radius: 12px 0 0 12px;
        }

        .goal-tree-summary {
          grid-template-columns: 1fr;
        }

        .goal-tree-body {
          min-height: 0;
        }
      }
    `,
  ];

  @property({ attribute: false })
  accessor goalTree: GoalTreeEnvelope | null = null;

  @property({ attribute: false })
  accessor goalStatus: string | null = null;

  @property({ attribute: false })
  accessor goalHistory: readonly GoalListSummary[] = [];

  @property({ type: Boolean })
  accessor goalHistoryLoading = false;

  @property({ attribute: false })
  accessor onClose: (() => void) | null = null;

  @property({ attribute: false })
  accessor onPatch: ((patch: GoalTreePatch) => void | Promise<void>) | null =
    null;

  @property({ attribute: false })
  accessor onRevalidate: (() => void) | null = null;

  @property({ attribute: false })
  accessor onResume: (() => void) | null = null;

  @property({ attribute: false })
  accessor onPause: (() => void) | null = null;

  @property({ attribute: false })
  accessor onCancel: (() => void) | null = null;

  @property({ attribute: false })
  accessor onSelectGoal: ((goalId: string) => void | Promise<void>) | null =
    null;

  @state()
  private accessor expandedNodeIds = new Set<string>();

  @state()
  private accessor selectedNodeId: string | null = null;

  @state()
  private accessor lastPatchError: string | null = null;

  @state()
  private accessor nodeInputValues = new Map<string, string>();

  @state()
  private accessor historyOpen = false;

  @state()
  private accessor actionInFlight = false;

  @state()
  private accessor actionError: string | null = null;

  private readonly toggleNode = (nodeId: string) => {
    const next = new Set(this.expandedNodeIds);
    if (next.has(nodeId)) next.delete(nodeId);
    else next.add(nodeId);
    this.expandedNodeIds = next;
  };

  protected override updated(changed: Map<PropertyKey, unknown>) {
    if (changed.has('goalTree')) {
      this.syncSelectionForGoalTree();
    }
  }

  private syncSelectionForGoalTree() {
    if (!this.goalTree) {
      this.selectedNodeId = null;
      this.expandedNodeIds = new Set();
      return;
    }
    const path = findPreferredNodePath(this.goalTree);
    if (!path) return;
    this.selectedNodeId = path.at(-1) ?? null;
    this.expandedNodeIds = new Set([
      ...this.expandedNodeIds,
      ...path.slice(0, -1),
    ]);
  }

  private renderNodeIcon(node: GoalTreeNode, active = false) {
    return html`<span
      class="goal-tree-status-dot"
      data-tone=${active ? 'accent' : nodeStatusTone(node.status)}
      title=${active
        ? I18n.t('com.streamlain.ai.target.status.running')
        : goalStatusLabel(node.status)}
    ></span>`;
  }

  private renderNodeAction(node: GoalTreeNode) {
    const modelLabel = effectiveStepModelLabel(
      node.modelPolicy?.userOverride?.tier ?? node.modelPolicy?.defaultTier,
      node.modelPolicy?.userOverride?.reasoning ?? node.modelPolicy?.reasoning
    );
    if (!modelLabel) return nothing;
    return html`<span class="goal-tree-row-action">
      ${modelLabel ? html`<span>${modelLabel}</span>` : nothing}
    </span>`;
  }

  private renderTree() {
    if (!this.goalTree) {
      return html`<div class="goal-tree-empty">
        ${I18n.t('com.streamlain.ai.target.empty')}
      </div>`;
    }
    const model = createGoalTreeViewModel({
      tree: this.goalTree,
      expandedNodeIds: this.expandedNodeIds,
      activeNodeId: resolveActiveGoalNodeId(this.goalTree, this.goalStatus),
    });
    return html`
      <div
        class="goal-tree-summary"
        aria-label=${I18n.t('com.streamlain.ai.target.progress.aria')}
      >
        <div class="goal-tree-metric">
          <div class="goal-tree-metric-label">
            ${I18n.t('com.streamlain.ai.target.progress')}
          </div>
          <div class="goal-tree-metric-value">${model.progressLabel}</div>
        </div>
        <div class="goal-tree-metric">
          <div class="goal-tree-metric-label">
            ${I18n.t('com.streamlain.ai.target.blocking')}
          </div>
          <div class="goal-tree-metric-value">${model.gapsBlocking}</div>
        </div>
        <div class="goal-tree-metric">
          <div class="goal-tree-metric-label">
            ${I18n.t('com.streamlain.ai.target.open')}
          </div>
          <div class="goal-tree-metric-value">${model.gapsWarning}</div>
        </div>
      </div>
      <div class="goal-tree-body streamlain-nested-display-list">
        ${model.nodes.map(item =>
          renderStreamLainNestedDisplayItemRow({
            action: this.renderNodeAction(item.node),
            className: `goal-tree-node-row${item.active ? ' goal-tree-node-active' : ''}`,
            depth: item.depth,
            expanded: item.hasChildren ? item.expanded : undefined,
            icon: this.renderNodeIcon(item.node, item.active),
            onClick: event => {
              event.preventDefault();
              if (item.hasChildren) this.toggleNode(item.node.nodeId);
              this.selectedNodeId = item.node.nodeId;
            },
            title: item.label,
            subtitle: item.subtitle,
            titleAttr: item.ariaLabel,
            treeGuideDepths: item.treeGuideDepths,
            treeLast: item.treeLast,
            treeToggleStyle: 'plus-circle',
          })
        )}
      </div>
      ${this.renderDetail()}
    `;
  }

  private renderDetail() {
    const node = this.selectedNodeId
      ? findNode(this.goalTree?.root ?? null, this.selectedNodeId)
      : null;
    if (!node) return nothing;
    return html`<section
      class="goal-tree-detail"
      aria-label=${I18n.t('com.streamlain.ai.target.step-detail.aria')}
    >
      <div>
        <div class="goal-tree-detail-title">${goalNodeLabel(node)}</div>
        <div class="goal-tree-detail-meta">${goalStatusLabel(node.status)}</div>
      </div>
      <div class="goal-tree-detail-content">
        ${this.renderNodeGaps(node)} ${this.renderNodeValueInput(node)}
        ${this.goalTree &&
        node.modelPolicy &&
        isGoalTreeModelPolicyEditableNode(this.goalTree, node)
          ? html`<goal-step-model-controls
              .policy=${node.modelPolicy}
              .onPolicyChange=${(policy: GoalStepModelPolicy) =>
                this.emitModelPolicyPatch(node, policy)}
            ></goal-step-model-controls>`
          : nothing}
        ${this.lastPatchError
          ? html`<div class="goal-tree-detail-error" role="alert">
              ${this.lastPatchError}
            </div>`
          : nothing}
      </div>
    </section>`;
  }

  private renderNodeGaps(node: GoalTreeNode) {
    const gaps =
      this.goalTree?.gaps.filter(gap => gap.nodeId === node.nodeId) ?? [];
    if (gaps.length === 0) return nothing;
    return html`<div class="goal-tree-detail-section">
      ${gaps.map(
        gap =>
          html`<div class="goal-tree-detail-text">
            ${gap.blocking
              ? I18n.t('com.streamlain.ai.target.blocking')
              : I18n.t('com.streamlain.ai.target.open')}:
            ${gap.reason}
          </div>`
      )}
    </div>`;
  }

  private renderNodeValueInput(node: GoalTreeNode) {
    const detail = isRecord(node.detail) ? node.detail : {};
    const canSetValue = canSetNodeValue(this.goalTree, node);
    const canBind = canBindEntity(this.goalTree, node);
    if (!canSetValue && !canBind) return nothing;
    const value = this.nodeInputValues.get(node.nodeId) ?? '';
    const options = readAllowedValues(detail);
    if (canBind && isWorkspaceBindingNode(detail, node)) {
      return html`<div class="goal-tree-detail-input">
        <label class="goal-tree-detail-label" for=${`${node.nodeId}-entity`}>
          ${I18n.t('com.streamlain.ai.target.input.entity')}
        </label>
        <input
          id=${`${node.nodeId}-entity`}
          aria-label=${I18n.t('com.streamlain.ai.target.input.entity-for', {
            label: node.label,
          })}
          placeholder=${I18n.t('com.streamlain.ai.target.input.entity-id')}
          .value=${value}
          @input=${(event: InputEvent) =>
            this.setNodeInputValue(
              node.nodeId,
              (event.currentTarget as HTMLInputElement).value
            )}
        />
        <div class="goal-tree-detail-actions">
          <button type="button" @click=${() => this.emitBindEntity(node)}>
            ${I18n.t('com.streamlain.ai.target.action.bind-entity')}
          </button>
        </div>
      </div>`;
    }
    if (options.length > 0 && canSetValue) {
      const selected = value || options[0].value;
      return html`<div class="goal-tree-detail-input">
        <label class="goal-tree-detail-label" for=${`${node.nodeId}-select`}>
          ${I18n.t('com.streamlain.ai.target.input.select-value')}
        </label>
        <select
          id=${`${node.nodeId}-select`}
          aria-label=${I18n.t('com.streamlain.ai.target.input.select-for', {
            label: node.label,
          })}
          .value=${selected}
          @change=${(event: Event) =>
            this.setNodeInputValue(
              node.nodeId,
              (event.currentTarget as HTMLSelectElement).value
            )}
        >
          ${options.map(
            option =>
              html`<option value=${option.value}>${option.label}</option>`
          )}
        </select>
        <div class="goal-tree-detail-actions">
          <button
            type="button"
            @click=${() =>
              this.emitSetNodeValue(
                node,
                this.nodeInputValues.get(node.nodeId) || selected
              )}
          >
            ${I18n.t('com.streamlain.ai.target.action.apply-selection')}
          </button>
        </div>
      </div>`;
    }
    if (isBooleanNode(detail) && canSetValue) {
      const checked =
        value === ''
          ? readBooleanDetailValue(detail)
          : value === 'true' || value === '1';
      return html`<div class="goal-tree-detail-input">
        <label class="goal-tree-detail-toggle">
          <input
            type="checkbox"
            .checked=${checked}
            @change=${(event: Event) =>
              this.setNodeInputValue(
                node.nodeId,
                (event.currentTarget as HTMLInputElement).checked
                  ? 'true'
                  : 'false'
              )}
          />
          <span>${node.label}</span>
        </label>
        <div class="goal-tree-detail-actions">
          <button
            type="button"
            @click=${() =>
              this.emitSetNodeValue(
                node,
                (this.nodeInputValues.get(node.nodeId) ?? String(checked)) ===
                  'true'
              )}
          >
            ${I18n.t('com.streamlain.ai.target.action.apply-toggle')}
          </button>
        </div>
      </div>`;
    }
    return html`<div class="goal-tree-detail-input">
      <input
        aria-label=${I18n.t('com.streamlain.ai.target.input.value-for', {
          label: node.label,
        })}
        .value=${value}
        @input=${(event: InputEvent) =>
          this.setNodeInputValue(
            node.nodeId,
            (event.currentTarget as HTMLInputElement).value
          )}
      />
      <div class="goal-tree-detail-actions">
        <button type="button" @click=${() => this.emitSetNodeValue(node)}>
          ${I18n.t('com.streamlain.ai.target.action.apply-value')}
        </button>
      </div>
    </div>`;
  }

  private async emitSetNodeValue(
    node: GoalTreeNode,
    valueOverride?: string | boolean
  ) {
    if (!this.goalTree || !this.onPatch) return;
    await this.safePatch({
      domain: this.goalTree.domain,
      commands: [
        {
          type: 'setNodeValue',
          nodeId: node.nodeId,
          value: valueOverride ?? this.nodeInputValues.get(node.nodeId) ?? '',
        },
      ],
    });
  }

  private async emitBindEntity(node: GoalTreeNode) {
    if (!this.goalTree || !this.onPatch) return;
    const value = this.nodeInputValues.get(node.nodeId)?.trim();
    if (!value) return;
    const detail = isRecord(node.detail) ? node.detail : {};
    await this.safePatch({
      domain: this.goalTree.domain,
      commands: [
        {
          type: 'bindEntity',
          nodeId: node.nodeId,
          entity: {
            id: value,
            type: readString(detail.entityType) ?? node.kind,
            label: value,
          },
        },
      ],
    });
  }

  private async emitModelPolicyPatch(
    node: GoalTreeNode,
    policy: GoalStepModelPolicy
  ) {
    if (!this.goalTree || !this.onPatch) return;
    if (!isGoalTreeModelPolicyEditableNode(this.goalTree, node)) return;
    await this.safePatch({
      domain: this.goalTree.domain,
      commands: [
        {
          type: 'setStepModelPolicy',
          nodeId: node.nodeId,
          policy,
        },
      ],
    });
  }

  private async safePatch(patch: GoalTreePatch) {
    if (!this.onPatch) return;
    this.lastPatchError = null;
    try {
      await this.onPatch(patch);
    } catch (error) {
      this.lastPatchError =
        error instanceof Error
          ? error.message
          : I18n.t('com.streamlain.ai.target.update-failed');
    }
  }

  private setNodeInputValue(nodeId: string, value: string) {
    const next = new Map(this.nodeInputValues);
    next.set(nodeId, value);
    this.nodeInputValues = next;
  }

  private handleKeyDown(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      event.stopPropagation();
      this.onClose?.();
    }
  }

  private async runAction(action: () => void | Promise<void>) {
    if (this.actionInFlight) return;
    this.actionInFlight = true;
    this.actionError = null;
    try {
      await action();
    } catch (error) {
      this.actionError =
        error instanceof Error
          ? error.message
          : I18n.t('com.streamlain.ai.target.update-failed');
    } finally {
      this.actionInFlight = false;
    }
  }

  private renderGoalHistory() {
    if (this.goalHistoryLoading && this.goalHistory.length === 0) {
      return html`<div class="goal-tree-empty">
        ${I18n.t('com.streamlain.ai.target.history.loading')}
      </div>`;
    }
    if (this.goalHistory.length === 0) {
      return html`<div class="goal-tree-empty">
        ${I18n.t('com.streamlain.ai.target.history.empty')}
      </div>`;
    }
    return html`<div class="goal-tree-history">
      ${this.goalHistory.map(goal => {
        const progress = goal.goalTree?.progress;
        const completed = progress
          ? progress.configured + progress.defaulted + (progress.completed ?? 0)
          : 0;
        return html`<button
          class="goal-tree-history-item"
          type="button"
          ?disabled=${!goal.goalTree}
          @click=${() =>
            void this.runAction(async () => {
              if (!goal.goalTree) return;
              await this.onSelectGoal?.(goal.goalId);
              this.historyOpen = false;
            })}
        >
          <span
            class="goal-tree-status-dot"
            data-tone=${goalRunStatusTone(goal.status)}
          ></span>
          <span>
            <span class="goal-tree-history-title"
              >${goal.objective ??
              I18n.t('com.streamlain.ai.target.label')}</span
            >
            <span class="goal-tree-history-meta"
              >${goalRunStatusLabel(goal.status)}</span
            >
          </span>
          <span class="goal-tree-history-progress"
            >${progress ? `${completed}/${progress.total}` : ''}</span
          >
        </button>`;
      })}
    </div>`;
  }

  override render() {
    const status = this.goalStatus ?? '';
    const canPause = status === 'queued' || status === 'running';
    const canResume = status === 'paused';
    const canCancel =
      !!status &&
      status !== 'completed' &&
      status !== 'failed' &&
      status !== 'cancelled';
    return html`<div
        class="goal-tree-backdrop"
        @click=${() => this.onClose?.()}
      ></div>
      <aside
        class="goal-tree-panel"
        role="dialog"
        aria-modal="true"
        aria-label=${I18n.t('com.streamlain.ai.target.label')}
        tabindex="-1"
        @keydown=${this.handleKeyDown}
      >
        <div class="goal-tree-header">
          <div class="goal-tree-header-main">
            <button
              class="goal-tree-control goal-tree-history-back"
              type="button"
              title=${I18n.t('com.streamlain.ai.target.history.label')}
              aria-label=${I18n.t('com.streamlain.ai.target.history.label')}
              @click=${() => (this.historyOpen = !this.historyOpen)}
            >
              ${this.historyOpen
                ? I18n.t('com.streamlain.ai.target.history.back')
                : I18n.t('com.streamlain.ai.target.history.label')}
            </button>
            <div class="goal-tree-header-copy">
              <div class="goal-tree-title">
                ${this.historyOpen
                  ? I18n.t('com.streamlain.ai.target.history.label')
                  : (this.goalTree?.root.label ??
                    I18n.t('com.streamlain.ai.target.label'))}
              </div>
              <div class="goal-tree-subtitle">
                ${this.goalTree
                  ? goalModeLabelForDisplay(
                      this.goalTree.domain,
                      this.goalTree.mode
                    )
                  : I18n.t('com.streamlain.ai.target.no-active')}
              </div>
            </div>
          </div>
          <div class="goal-tree-header-actions">
            ${canPause && this.onPause
              ? html`<button
                  class="goal-tree-control"
                  type="button"
                  title=${I18n.t('com.streamlain.ai.target.action.pause')}
                  aria-label=${I18n.t('com.streamlain.ai.target.action.pause')}
                  ?disabled=${this.actionInFlight}
                  @click=${() => void this.runAction(() => this.onPause?.())}
                >
                  ${PausePanelIcon()}
                </button>`
              : nothing}
            ${canResume && this.onResume
              ? html`<button
                  class="goal-tree-control"
                  type="button"
                  title=${I18n.t('com.streamlain.ai.target.action.resume')}
                  aria-label=${I18n.t('com.streamlain.ai.target.action.resume')}
                  ?disabled=${this.actionInFlight}
                  @click=${() => void this.runAction(() => this.onResume?.())}
                >
                  ${PlayIcon()}
                </button>`
              : nothing}
            ${canCancel && this.onCancel
              ? html`<button
                  class="goal-tree-control"
                  type="button"
                  title=${I18n.t('com.streamlain.ai.target.action.cancel')}
                  aria-label=${I18n.t('com.streamlain.ai.target.action.cancel')}
                  ?disabled=${this.actionInFlight}
                  @click=${() => void this.runAction(() => this.onCancel?.())}
                >
                  ${StopIcon()}
                </button>`
              : nothing}
            <button
              class="goal-tree-close"
              type="button"
              aria-label=${I18n.t('com.streamlain.ai.target.close')}
              @click=${() => this.onClose?.()}
            >
              ${CloseIcon()}
            </button>
          </div>
        </div>
        ${this.actionError
          ? html`<div class="goal-tree-action-error" role="alert">
              ${this.actionError}
            </div>`
          : nothing}
        ${this.historyOpen ? this.renderGoalHistory() : this.renderTree()}
      </aside>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'goal-tree-panel': GoalTreePanel;
  }
}

function findNode(
  node: GoalTreeNode | null,
  nodeId: string
): GoalTreeNode | null {
  if (!node) return null;
  if (node.nodeId === nodeId) return node;
  for (const child of node.children) {
    const found = findNode(child, nodeId);
    if (found) return found;
  }
  return null;
}

export function findPreferredNodePath(tree: GoalTreeEnvelope): string[] | null {
  const blockingGapNodeIds = new Set(
    tree.gaps.filter(gap => gap.blocking).map(gap => gap.nodeId)
  );
  const gapNodeIds = new Set(tree.gaps.map(gap => gap.nodeId));
  return (
    findFirstNodePath(
      tree.root,
      node =>
        blockingGapNodeIds.has(node.nodeId) ||
        node.status === 'invalid' ||
        node.status === 'blocked'
    ) ??
    findFirstNodePath(
      tree.root,
      node => gapNodeIds.has(node.nodeId) || node.status === 'missing'
    ) ?? [tree.root.nodeId]
  );
}

function findFirstNodePath(
  node: GoalTreeNode,
  predicate: (node: GoalTreeNode) => boolean,
  path: readonly string[] = []
): string[] | null {
  const nextPath = [...path, node.nodeId];
  if (predicate(node)) return nextPath;
  for (const child of node.children) {
    const found = findFirstNodePath(child, predicate, nextPath);
    if (found) return found;
  }
  return null;
}

function canSetNodeValue(
  tree: GoalTreeEnvelope | null,
  node: GoalTreeNode
): boolean {
  if (node.status === 'missing' || node.status === 'invalid') return true;
  const actions = new Set([
    ...(node.actions ?? []).map(action => action.kind),
    ...(tree?.nextSteps ?? [])
      .filter(step => step.nodeId === node.nodeId)
      .map(step => step.actionKind),
    ...(tree?.gaps ?? [])
      .filter(gap => gap.nodeId === node.nodeId)
      .flatMap(gap => gap.allowedActions),
  ]);
  return (
    actions.has('setNodeValue') ||
    actions.has('set_intent') ||
    actions.has('choose_typed_value') ||
    actions.has('provide_override_reason') ||
    actions.has('provide_input') ||
    actions.has('fix_step')
  );
}

function canBindEntity(
  tree: GoalTreeEnvelope | null,
  node: GoalTreeNode
): boolean {
  const actions = new Set([
    ...(node.actions ?? []).map(action => action.kind),
    ...(tree?.nextSteps ?? [])
      .filter(step => step.nodeId === node.nodeId)
      .map(step => step.actionKind),
    ...(tree?.gaps ?? [])
      .filter(gap => gap.nodeId === node.nodeId)
      .flatMap(gap => gap.allowedActions),
  ]);
  return (
    actions.has('bindEntity') ||
    actions.has('bind_workspace_entity') ||
    actions.has('provide_input')
  );
}

export function canAcceptDefault(
  tree: GoalTreeEnvelope | null,
  node: GoalTreeNode
): boolean {
  const detail = isRecord(node.detail) ? node.detail : {};
  const actions = new Set([
    ...(node.actions ?? []).map(action => action.kind),
    ...(tree?.nextSteps ?? [])
      .filter(step => step.nodeId === node.nodeId)
      .map(step => step.actionKind),
    ...(tree?.gaps ?? [])
      .filter(gap => gap.nodeId === node.nodeId)
      .flatMap(gap => gap.allowedActions),
  ]);
  return (
    node.status === 'defaulted' ||
    Object.prototype.hasOwnProperty.call(detail, 'defaultValue') ||
    actions.has('acceptDefault') ||
    actions.has('accept_default')
  );
}

function readAllowedValues(
  detail: Record<string, unknown>
): Array<{ label: string; value: string }> {
  if (!Array.isArray(detail.allowedValues)) return [];
  return detail.allowedValues
    .map(value => {
      if (
        typeof value === 'string' ||
        typeof value === 'number' ||
        typeof value === 'boolean'
      ) {
        return { label: String(value), value: String(value) };
      }
      if (isRecord(value)) {
        const id = readString(value.id) ?? readString(value.value);
        const label = readString(value.label) ?? readString(value.name) ?? id;
        if (id && label) return { label, value: id };
      }
      return null;
    })
    .filter((value): value is { label: string; value: string } => !!value);
}

function isWorkspaceBindingNode(
  detail: Record<string, unknown>,
  node: GoalTreeNode
): boolean {
  return (
    detail.valueKind === 'workspaceBinding' ||
    detail.valueKind === 'entity' ||
    node.kind.includes('binding')
  );
}

function isBooleanNode(detail: Record<string, unknown>): boolean {
  return (
    detail.valueKind === 'toggle' ||
    detail.valueKind === 'boolean' ||
    typeof detail.value === 'boolean' ||
    typeof detail.defaultValue === 'boolean'
  );
}

function readBooleanDetailValue(detail: Record<string, unknown>): boolean {
  if (typeof detail.value === 'boolean') return detail.value;
  if (typeof detail.defaultValue === 'boolean') return detail.defaultValue;
  return false;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function readString(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value : null;
}

function goalRunStatusTone(status?: string | null): string {
  if (status === 'completed') return 'positive';
  if (status === 'failed' || status === 'cancelled') return 'critical';
  if (status === 'waiting_for_confirmation' || status === 'waiting_for_user') {
    return 'warning';
  }
  if (status === 'queued' || status === 'running') return 'accent';
  return 'muted';
}

function goalRunStatusLabel(status?: string | null): string {
  const key =
    status === 'queued' ||
    status === 'running' ||
    status === 'paused' ||
    status === 'completed' ||
    status === 'failed' ||
    status === 'cancelled' ||
    status === 'waiting_for_confirmation' ||
    status === 'waiting_for_user'
      ? status.replaceAll('_', '-')
      : 'unknown';
  return I18n.t(`com.streamlain.ai.target.run-status.${key}`);
}
